import * as Tabs from '@radix-ui/react-tabs';
import { lazy, Suspense, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import PANEL_CAPABILITIES from '../../shared/panelCapabilities';
import { InspectionSettingsProvider } from '../../contexts/InspectionSettingsContext';
import OversightTab from './OversightTab';
import AiTab from './AiTab';
import AvaNotificationToasts from './ava/AvaNotificationToasts';
import CoverageAdminTab from './coverage/CoverageAdminTab';
import SubscriptionHealthBanner from './SubscriptionHealthBanner';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import OpenRouterBalanceWidget from './OpenRouterBalanceWidget';
import '../../styles/AdminPage.css';
// Register the inert owned POP dispatcher before BrowserRouter mounts.
import './madar/MadarWorkspaceContext';
import { CarrierContext, useCarrier } from '../../contexts/CarrierContext';
import { CarrierScope } from '../carrier/CarrierShell';
import CarrierSwitcher from '../carrier/CarrierSwitcher';
import CarrierBadge from '../carrier/CarrierBadge';

const MadarTab = lazy(() => import('./madar/MadarTab'));
const CarrierRulesTab = lazy(() => import('./carrier/CarrierRulesTab'));
const OperatorsTab = lazy(() => import('./OperatorsTab'));
const ExpertsTab = lazy(() => import('./ExpertsTab'));
const InstallerManagementTab = lazy(() => import('./installers/InstallerManagementTab'));
const CustomerRegistrationsPanel = lazy(() => import('./customer/CustomerRegistrationsPanel'));
const StoreTab = lazy(() => import('./front/StoreTab'));
const ContentTab = lazy(() => import('./front/ContentTab'));
const SalesTab = lazy(() => import('./front/SalesTab'));
const FinanceTab = lazy(() => import('./front/FinanceTab'));
const SettingsTab = lazy(() => import('./front/SettingsTab'));
const UsersTab = lazy(() => import('./users/UsersTab'));
const TicketsSubtab = lazy(() => import('./support/TicketsSubtab'));

function AdminLoading() {
  return <div className="admin-empty" role="status">در حال آماده‌سازی بخش مدیریتی…</div>;
}

const ADMIN_TABS = Object.freeze([
  { value: 'coverage', label: 'پوشش', capability: 'coverage', group: 'network' },
  { value: 'madar', label: 'مدار', capability: 'madar', group: 'network' },
  { value: 'carrier', label: 'پروفایل اپراتور', capability: 'carrier', group: 'network' },
  { value: 'operators', label: 'اپراتورهای شبکه', capability: 'catalogue', group: 'network' },
  { value: 'experts', label: 'کارشناسان', capability: 'experts', group: 'field' },
  { value: 'installers', label: 'نیروهای اجرایی', capability: 'installers', group: 'field' },
  { value: 'oversight', label: 'نظارت', capability: 'oversight', group: 'field' },
  { value: 'store', label: 'فروشگاه', capability: 'store', group: 'market' },
  { value: 'content', label: 'محتوا', capability: 'content', group: 'market' },
  { value: 'applicants', label: 'متقاضیان', capability: 'applicants', group: 'market' },
  { value: 'sales', label: 'فروش', capability: 'sales', group: 'market' },
  { value: 'finance', label: 'مالی', capability: 'finance', group: 'market' },
  { value: 'support', label: 'پشتیبانی', capability: 'support', group: 'market' },
  { value: 'ai', label: 'هوش مصنوعی', capability: 'ai', group: 'system' },
  { value: 'users', label: 'کاربران', capability: 'users', group: 'system' },
  { value: 'settings', label: 'تنظیمات', capability: 'settings', group: 'system' },
]);

// Top-level admin navigation is arranged by operational domain. Carrier data is
// «اپراتورهای شبکه», while the people who draw and review polygons are «کارشناسان».
// Stateful coverage, oversight, customer-advisor, and Ava panels remain mounted so
// in-flight work survives a switch to another top-level tab.
export default function AdminPage() {
  const { profile, signOut } = useAuth();
  const { tab, sub } = useParams();
  const location = useLocation();
  const { confirmLeave } = useCarrier();
  const navigate = useNavigate();
  const onLogout = async () => { await signOut(); navigate('/login', { replace: true }); };
  const { canAccessPanelSection } = PANEL_CAPABILITIES;
  const role = profile?.role;
  const visibleTabs = ADMIN_TABS.filter(({ capability }) => (
    canAccessPanelSection(role, capability)
  ));
  const visibleValues = new Set(visibleTabs.map(({ value }) => value));
  const defaultTab = visibleTabs.length === 1
    ? visibleTabs[0].value
    : (visibleValues.has('experts') ? 'experts' : visibleTabs[0]?.value);
  const activeTab = visibleValues.has(tab) ? tab : defaultTab;

  // Preserve carrier context on legacy bookmarks before resolving the tab.
  if (tab === 'design') {
    const domain = sub === 'profiles' ? 'design' : 'tariffs';
    return <Navigate to={`/admin/carrier/${domain}${location.search}`} replace />;
  }
  if (tab === 'installers' && sub === 'workflow') {
    return <Navigate to={`/admin/carrier/installation${location.search}`} replace />;
  }
  if (tab === 'oversight' && sub === 'rulebooks') {
    return <Navigate to={`/admin/carrier/supervision${location.search}`} replace />;
  }

  if (visibleTabs.length > 0 && tab !== activeTab) {
    return <Navigate to={`/admin/${activeTab}${location.search}`} replace />;
  }

  return (
    <div className={`admin-root${activeTab === 'madar' ? ' iadmin' : ''}`}>
      {/* Proactive Ava notifications stay outside the tabs so reminders surface
          no matter which admin section is open. */}
      <AvaNotificationToasts />
      {/* One row, not four. The bar used to be two spans that wrapped on
          phones, which stacked brand / name+balance / workspace chips / logout
          into four centred lines and ate a third of the viewport before any
          content. Now the middle is a horizontal rail that scrolls, and the two
          things that must always be reachable - who you are, and the way out -
          sit outside it so they can never scroll off screen. */}
      <div className="admin-bar">
        <span className="admin-bar-brand">
          پنل ادمین<span className="admin-bar-brand-suffix"> - سامانهٔ FTTX</span>
        </span>
        <MadarCarrierSwitcher active={activeTab === 'madar'} />
        <span className="admin-bar-rail">
          <span className="admin-bar-user">{profile?.full_name || 'مدیر'}</span>
          <OpenRouterBalanceWidget />
          <WorkspaceSwitcher current="admin" />
        </span>
        <button type="button" className="admin-bar-exit" onClick={onLogout}>خروج</button>
      </div>
      <SubscriptionHealthBanner />
      <InspectionSettingsProvider>
        {visibleTabs.length === 0 ? (
          <div className="admin-body" role="status">
            شما به هیچ بخش مدیریتی دسترسی ندارید.
          </div>
        ) : (
          <Tabs.Root
            value={activeTab}
            onValueChange={async (value) => { if (await confirmLeave()) navigate(`/admin/${value}`); }}
            dir="rtl"
          >
            <div className="admin-tabs-row">
            <AdminTabRail activeTab={activeTab}>
              {visibleTabs.map(({ value, label, group }, index) => (
                <Tabs.Trigger
                  className="admin-tab"
                  value={value}
                  key={value}
                  data-tab-group={group}
                  data-group-start={index > 0 && group !== visibleTabs[index - 1].group ? '' : undefined}
                >
                  {label}
                </Tabs.Trigger>
              ))}
            </AdminTabRail>
            {['carrier', 'installers', 'oversight', 'madar'].includes(activeTab) && (
              <CarrierBadge className="carrier-context-tag" srPrefix="اپراتور فعال:" />
            )}
            </div>
          <div className={`admin-body${activeTab === 'madar' ? ' admin-body-madar' : ''}`}>
            {visibleValues.has('madar') && <Tabs.Content value="madar"><CarrierScope><Suspense fallback={<AdminLoading />}><MadarTab role={role} /></Suspense></CarrierScope></Tabs.Content>}
            {visibleValues.has('carrier') && <Tabs.Content value="carrier"><CarrierScope><Suspense fallback={<AdminLoading />}><CarrierRulesTab role={role} /></Suspense></CarrierScope></Tabs.Content>}
            {visibleValues.has('coverage') && (
              <Tabs.Content value="coverage" forceMount className="coverage-tab-content">
                <CoverageAdminTab role={role} />
              </Tabs.Content>
            )}
            {visibleValues.has('operators') && <Tabs.Content value="operators"><Suspense fallback={<AdminLoading />}><OperatorsTab /></Suspense></Tabs.Content>}
            {visibleValues.has('experts') && <Tabs.Content value="experts"><Suspense fallback={<AdminLoading />}><ExpertsTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('installers') && <Tabs.Content value="installers"><CarrierScope><Suspense fallback={<AdminLoading />}><InstallerManagementTab role={role} /></Suspense></CarrierScope></Tabs.Content>}
            {visibleValues.has('applicants') && (
              <Tabs.Content value="applicants"><Suspense fallback={<AdminLoading />}><CustomerRegistrationsPanel /></Suspense></Tabs.Content>
            )}
            {/* forceMount on oversight keeps its polling inspection subtabs alive. */}
            {visibleValues.has('oversight') && (
              <Tabs.Content value="oversight" forceMount className="oversight-tab-content">
                <CarrierScope><OversightTab role={role} /></CarrierScope>
              </Tabs.Content>
            )}
            {/* AiTab preserves the former customer-advisor and Ava forceMount
                behavior while grouping both with model settings. */}
            {visibleValues.has('ai') && (
              <Tabs.Content value="ai" forceMount className="ai-tab-content">
                <AiTab role={role} />
              </Tabs.Content>
            )}
            {visibleValues.has('store') && <Tabs.Content value="store"><Suspense fallback={<AdminLoading />}><StoreTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('content') && <Tabs.Content value="content"><Suspense fallback={<AdminLoading />}><ContentTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('sales') && <Tabs.Content value="sales"><Suspense fallback={<AdminLoading />}><SalesTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('finance') && <Tabs.Content value="finance"><Suspense fallback={<AdminLoading />}><FinanceTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('support') && <Tabs.Content value="support"><Suspense fallback={<AdminLoading />}><TicketsSubtab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('users') && <Tabs.Content value="users"><Suspense fallback={<AdminLoading />}><UsersTab role={role} /></Suspense></Tabs.Content>}
            {visibleValues.has('settings') && <Tabs.Content value="settings"><Suspense fallback={<AdminLoading />}><SettingsTab role={role} /></Suspense></Tabs.Content>}
            </div>
          </Tabs.Root>
        )}
      </InspectionSettingsProvider>
    </div>
  );
}

// CarrierContext owns the selection and dirty guard. Preserve only the Madar
// address here; no authentication, global carrier or unrelated tab behavior changes.
function MadarCarrierSwitcher({ active }) {
  const carrier = useCarrier(); const location = useLocation(); const navigate = useNavigate();
  if (!active) return <CarrierSwitcher />;
  const switchTo = async (slug) => {
    const address = { pathname: location.pathname, hash: location.hash };
    const query = new URLSearchParams(location.search);
    if (!await carrier.switchTo(slug)) return false;
    query.set('carrier', slug); query.delete('version');
    navigate({ ...address, search: `?${query}` }, { replace: true });
    return true;
  };
  return <CarrierContext.Provider value={{ ...carrier, switchTo }}><CarrierSwitcher /></CarrierContext.Provider>;
}

// Keep the carrier tag on its own grid track. Only this inner tab viewport
// scrolls; controls use physical geometry so RTL scrollLeft models do not leak
// into boundary detection or active-tab visibility.
function AdminTabRail({ activeTab, children }) {
  const list = useRef(null); const [edges, setEdges] = useState({ start: false, end: false });
  const measure = useCallback(() => {
    const node = list.current; if (!node?.firstElementChild) return;
    const bounds = node.getBoundingClientRect(); const style = getComputedStyle(node);
    const padRight = parseFloat(style.paddingRight || 0); const padLeft = parseFloat(style.paddingLeft || 0);
    const first = node.firstElementChild.getBoundingClientRect(); const last = node.lastElementChild.getBoundingClientRect();
    // Need for the controls is decided on the whole rail, not on the viewport the visible controls already narrow;
    // once they hide, a list that fits has nothing to scroll and the browser returns it to its start.
    const railWidth = node.closest('.admin-tab-rail')?.getBoundingClientRect().width ?? bounds.width;
    const fits = first.right - last.left + padRight + padLeft <= railWidth + 1;
    const start = !fits && first.right > bounds.right - padRight + 1;
    const end = !fits && last.left < bounds.left + padLeft - 1;
    setEdges((previous) => previous.start === start && previous.end === end ? previous : { start, end });
  }, []);
  const revealActive = useCallback(() => {
    const node = list.current; const active = node?.querySelector('[aria-selected="true"]');
    if (!active) { measure(); return; }
    const bounds = node.getBoundingClientRect(); const tab = active.getBoundingClientRect(); const style = getComputedStyle(node);
    const left = bounds.left + parseFloat(style.paddingLeft || 0); const right = bounds.right - parseFloat(style.paddingRight || 0);
    // Prefer the start of the list whenever the active tab fits there, so early tabs are never hidden needlessly.
    const first = node.firstElementChild.getBoundingClientRect();
    if (first.right - tab.left <= right - left) { if (Math.abs(first.right - right) > 1) node.scrollBy?.({ left: first.right - right, behavior: 'instant' }); }
    else if (tab.left < left) node.scrollBy?.({ left: tab.left - left, behavior: 'instant' });
    else if (tab.right > right) node.scrollBy?.({ left: tab.right - right, behavior: 'instant' });
    measure();
  }, [measure]);
  useLayoutEffect(() => {
    let live = true; const node = list.current;
    revealActive();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(revealActive) : null;
    observer?.observe(node); for (const child of node.children) observer?.observe(child);
    window.addEventListener('resize', revealActive);
    document.fonts?.ready.then(() => { if (live) revealActive(); });
    return () => { live = false; observer?.disconnect(); window.removeEventListener('resize', revealActive); };
  }, [activeTab, revealActive]);
  const scroll = (start) => {
    const node = list.current; const width = node.getBoundingClientRect().width;
    node.scrollBy({ left: (start ? 1 : -1) * Math.max(44, width * 0.75), behavior: 'instant' }); measure();
  };
  const overflow = edges.start || edges.end;
  return <div className="admin-tab-rail" dir="rtl">
    <button type="button" className="admin-tab-scroll" aria-label="نمایش برگه‌های ابتدای فهرست" hidden={!overflow}
      disabled={!edges.start} tabIndex={edges.start ? 0 : -1} onClick={() => scroll(true)}><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 5 7 7-7 7" /></svg></button>
    <div className="admin-tab-viewport" data-clipped-start={edges.start || undefined} data-clipped-end={edges.end || undefined}>
      <Tabs.List ref={list} className="admin-tabs" aria-label="بخش‌های اصلی پنل ادمین" onScroll={measure}>{children}</Tabs.List>
    </div>
    <button type="button" className="admin-tab-scroll" aria-label="نمایش برگه‌های انتهای فهرست" hidden={!overflow}
      disabled={!edges.end} tabIndex={edges.end ? 0 : -1} onClick={() => scroll(false)}><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 5-7 7 7 7" /></svg></button>
  </div>;
}
